import { BarChart3, Megaphone, Send, Store, Users, Users2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { buyerTeamsApi } from "@/api/buyerTeams";
import { PageHeader } from "@/components/layout/PageHeader";
import { EntityTabBar, type EntityTabItem } from "@/components/layout/EntityTabBar";
import { Select } from "@/components/ui/Select";
import { buyerTeamKeys } from "@/hooks/buyers/buyerKeys";
import { SkeletonList } from "@/design-system/components/ui";
import { useMyTeamBoard } from "@/hooks/buyers/useBuyerReports";
import { useAuth } from "@/hooks/useAuth";
import { monthLabel } from "../indexTiles/tileBits";
import {
  parsePeriod,
  parseReportTab,
  periodOptions,
  type ReportTabKey,
} from "./reportFilters";
import { AdvertisingReportTab } from "./tabs/AdvertisingReportTab";
import { BuyersReportTab } from "./tabs/BuyersReportTab";
import { ListingsReportTab } from "./tabs/ListingsReportTab";
import { SpecialistsReportTab } from "./tabs/SpecialistsReportTab";
import { SubmissionsReportTab } from "./tabs/SubmissionsReportTab";

const TAB_ITEMS: ReadonlyArray<EntityTabItem<ReportTabKey>> = [
  { key: "buyers", label: "Buyers", icon: Users },
  { key: "listings", label: "Listings", icon: Store },
  { key: "submissions", label: "Submissions", icon: Send },
  { key: "specialists", label: "Consultants", icon: Users2 },
  { key: "advertising", label: "Advertising", icon: Megaphone },
];

/**
 * Buyer reports (WS-B4): the tile decks the three list pages used to carry,
 * the team board, and the cost-per-lead report, on one `?tab=` surface.
 *
 * <p><b>Team scope comes from the caller's shape, not from a prop.</b> A
 * `Buyers.ManageAllTeams` holder gets a Team select (`?team=`, default "All
 * teams"); everybody else reads their OWN team, which the client learns from
 * the caller's-own-board read — there is no other endpoint that says which
 * team a person is on — and no select is rendered, because a control that
 * cannot change what it offers to change teaches the reader the surface is
 * lying (the queue page's rule).</p>
 *
 * <p>Advertising is gated on `BuyerAdvertising.Read` and HIDDEN otherwise,
 * never disabled; its month range rides `?from=`/`?to=` and defaults to the
 * trailing three months.</p>
 */
export function BuyerReportsPage() {
  const { hasPermission } = useAuth();
  const canSeeAllTeams = hasPermission("Buyers.ManageAllTeams");
  const canSeeAdvertising = hasPermission("BuyerAdvertising.Read");

  const [params, setParams] = useSearchParams();
  const requestedTab = parseReportTab(params.get("tab"));
  const tab: ReportTabKey =
    requestedTab === "advertising" && !canSeeAdvertising ? "buyers" : requestedTab;
  const items = canSeeAdvertising ? TAB_ITEMS : TAB_ITEMS.filter((t) => t.key !== "advertising");

  // The caller's own team — resolved once, only for a one-team caller.
  const mine = useMyTeamBoard(!canSeeAllTeams);
  // The registry, only for the caller who gets a select — a one-team caller
  // has nothing to choose from, so the read is not made.
  const teams = useQuery({
    queryKey: buyerTeamKeys.list(false),
    queryFn: () => buyerTeamsApi.list(false),
    enabled: canSeeAllTeams,
  });

  const requestedTeam = params.get("team") ?? "";
  const teamId = canSeeAllTeams
    ? requestedTeam || undefined
    : mine.data?.teamId ?? undefined;
  const noTeamReason: "pick-a-team" | "no-team" = canSeeAllTeams ? "pick-a-team" : "no-team";
  // A one-team caller's tabs wait for the team, or the stats would fetch once
  // unscoped and once scoped for every open of the page.
  const resolvingTeam = !canSeeAllTeams && mine.isLoading;

  const { from, to } = parsePeriod(params.get("from"), params.get("to"));
  const months = periodOptions();

  function set(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Buyer CRM"
        title="Reports"
        description="Funnel, attention and volume across buyers, listings and submissions; the team board; and what advertising is costing per lead."
        actions={
          canSeeAllTeams ? (
            <div className="flex items-center gap-space-2">
              <label htmlFor="reports-team-filter" className="text-caption text-gray-mid">
                Team
              </label>
              <Select
                id="reports-team-filter"
                aria-label="Team filter"
                className="w-52"
                value={requestedTeam}
                onChange={(e) => set("team", e.target.value)}
              >
                <option value="">All teams</option>
                {(teams.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
              {teams.isError && (
                <span className="text-caption text-status-error" role="alert">
                  Couldn&apos;t load teams.
                </span>
              )}
            </div>
          ) : undefined
        }
      />

      <EntityTabBar<ReportTabKey>
        ariaLabel="Report sections"
        testIdPrefix="buyer-reports"
        items={items}
        value={tab}
        onChange={(next) => set("tab", next)}
        showLabels
      />

      <div className="mt-space-4">
        {resolvingTeam && <SkeletonList rows={3} data-testid="reports-resolving-team" />}
        {!resolvingTeam && tab === "buyers" && <BuyersReportTab teamId={teamId} />}
        {tab === "listings" && <ListingsReportTab />}
        {tab === "submissions" && <SubmissionsReportTab />}
        {!resolvingTeam && tab === "specialists" && (
          <SpecialistsReportTab teamId={teamId} reason={noTeamReason} />
        )}
        {!resolvingTeam && tab === "advertising" && canSeeAdvertising && (
          <>
            <div className="flex flex-wrap items-center gap-space-3 mb-space-4">
              <BarChart3 className="h-4 w-4 text-gray-mid" aria-hidden />
              <label htmlFor="reports-from" className="text-caption text-gray-mid">
                From
              </label>
              <Select
                id="reports-from"
                aria-label="From month"
                className="w-36"
                value={from}
                onChange={(e) => set("from", e.target.value)}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </Select>
              <label htmlFor="reports-to" className="text-caption text-gray-mid">
                To
              </label>
              <Select
                id="reports-to"
                aria-label="To month"
                className="w-36"
                value={to}
                onChange={(e) => set("to", e.target.value)}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </Select>
            </div>
            <AdvertisingReportTab from={from} to={to} teamId={teamId} />
          </>
        )}
      </div>
    </>
  );
}
